<template>
	<v-menu v-model="menu" :close-on-content-click="false" :close-on-click="false" max-width="520">
		<template v-slot:activator="{ on }">
			<v-text-field
				v-if="!iconOnly"
				:value="dateRangeText"
				:class="whiteTheme ? 'white-theme' : ''"
				readonly
				clearable
				:label="label"
				prepend-icon="mdi-calendar-range"
				v-on="on"
				@click:clear="clear"
			></v-text-field>
			<v-icon
				v-else	
				color="white"
				v-on="on"
				@click:clear="clear"
			>
				mdi-calendar-range
			</v-icon>
		</template>
		<v-date-picker
			v-if="currentDateOnly"
			v-model="singleDate"
			header-color="primary"
			class="date-time-range-picker"
		>
			<div class="d-flex justify-end" style="width:100%;">
				<v-btn text color="primary" @click="cancel">Cancel</v-btn>
				<v-btn dark tile color="third" @click="setDate">Apply</v-btn>
			</div>
		</v-date-picker>
		<v-date-picker
			v-else
			v-model="dates"
			range
			full-width
			:landscape="$vuetify.breakpoint.smAndUp"
			header-color="primary"
			class="date-time-range-picker"
		>
			<v-row justify="space-around" dense>
				<v-col md="4">
					<v-text-field
						v-model="startTime"
						label="Start Time"
						type="time"
						prepend-icon="mdi-clock-start"
						dense
						:error="startErr"
						:error-messages="errorMessage"
						@click:prepend="mode = 0;dialog = true"
						@click:clear="startErr = false"
					></v-text-field>
				</v-col>
				<v-col md="4">
					<v-text-field
						v-model="endTime"
						label="End Time"
						type="time"
						prepend-icon="mdi-clock-end"
						dense
						:error="endErr"
						:error-messages="errorMessage"
						@click:prepend="mode = 1;dialog = true"
						@click:clear="endErr = false"
					></v-text-field>
				</v-col>
			</v-row>
			<div class="d-flex justify-end" style="width:100%;">
				<v-btn text color="primary" @click="cancel">Cancel</v-btn>
				<v-btn dark tile color="third" @click="setRange">Apply</v-btn>
			</div>
			<div :class="{ shrinkHeight : rangeLimited	 }" class="shortcuts">
				<v-btn
					v-for="(btn, i) in rangeShortcuts"
					:key="i"
					depressed
					color="third white--text"
					class="ml-0"
					@click="() => quickTime(btn)"
				>{{btn}}</v-btn>
			</div>
		</v-date-picker>

		<v-dialog
			ref="dialog"
			v-model="dialog"
			:return-value.sync="time"
			transition="scale-transition"
			persistent
			width="290px"
		>	
			<v-time-picker
				ref="timePicker"
				v-model="time"
				header-color="third"
				full-width
				scrollable
				ampm-in-title
			>
				<v-spacer></v-spacer>
				<div class="control-container">
					<v-btn text color="primary" @click="dialog = false">Discard</v-btn>
					<v-btn text color="primary" @click="setTime">SAVE</v-btn>
				</div>
			</v-time-picker>
		</v-dialog>
	</v-menu>
</template>

<script>

  import moment from "moment";

	import { Logger } from "aws-amplify";
	const logger = new Logger("Date Time Range");

	export default {
		name: 'DateTimeRange',
		props: {
			startValue: {
				type: [Array, Boolean],
				required: false,
				default: () => false
			},
			iconOnly: {
				type: Boolean,
				required: false,
				default: () =>  false
			},
			rangeLimited: {
				type: Boolean,
				required: false,
				default: () => false 
			},
			currentDateOnly: {
				type: Boolean,
				required: false,
				default: () => false
			},
			whiteTheme: {
				type: Boolean,
				required: false,
				default: () => false
			},
			label: {
				required: false,
				default: () => '',
				type: String
			}
		},
		data: () => ({
			shortcuts: [
				"Today",
				"Yesterday",
				"Last 7 Days",
				"Last 30 Days",
				"This Month",
				"Last Month"
			],
			result: [],
			singleDate: '',
			dates: [],
			menu: false,
			dialog: false,
			time: null,
			mode: 0,
			startTime: null,
			endTime: null,
			endErr: false,
			startErr: false,
			errorMessage: []
		}),
		computed: {
			dateRangeText() {
				if (this.currentDateOnly) return moment(this.singleDate).format("D MMM YYYY");

				if (!this.dates || !this.dates.length) return "";

				let date = this.dates,
					res = "";
				if (
					this.dates.length === 1 ||
					(this.dates.length === 2 && this.dates[0] === this.dates[1])
				) {
					date = this.dates[0];
				}

				if (typeof date === "string") {
					if (this.startTime && this.endTime) {
						res = `${moment(date).format("D MMM YYYY")} ${
							this.startTime
						} - ${this.endTime}`;
					} else {
						res = `${moment(date).format("D MMM YYYY")}`;
					}
				} else {
					if (this.startTime && this.endTime) {
						res = `${moment(date[0]).format("D MMM YYYY")} ${
							this.startTime
						} - ${moment(date[1]).format("D MMM YYYY")} ${
							this.endTime
						}`;
					} else {
						res = `${moment(date[0]).format("D MMM YYYY")} - ${moment(
							date[1]
						).format("D MMM YYYY")}`;
					}
				}

				return res;
			},
			rangeShortcuts() {
				if (this.rangeLimited) {
					return [
						"Today",
						"Yesterday",
						"Last 7 Days"
					]
				}
				else if (this.currentDateOnly) {
					return [
						"Today"
					]
				} 
				else {
					return [
						"Today",
						"Yesterday",
						"Last 7 Days",
						"Last 30 Days",
						"This Month",
						"Last Month"
					]
				}
			}
		},
		watch: {
			startTime: "change",
			endTime: "change",
			dates: "change"
		},
		mounted () {
			if(this.startValue) {
				this.dates = this.startValue
				this.singleDate = this.startValue[0]
			}
		},
		methods: {
			cancel() {
				this.dates = [];
				this.startErr = false;
				this.endErr = false;
				this.startTime = null;
				this.endTime = null;
				this.errorMessage = [];
				this.menu = false;
			},
			clear() {
				this.$emit("change", false);
				this.dates = [];
			},
			change() {
				this.errorMessage = [];
				if (this.startErr) this.startErr = false;
				if (this.endErr) this.endErr = false;
			},
			setTime() {
				logger.debug(
					this.$refs,
					this.mode,
					this.time,
					this.startTime,
					this.endTime
				);
				if (this.mode) {
					this.endTime = this.time;
				} else {
					this.startTime = this.time;
				}
				this.$refs.timePicker.selecting = 1;
				this.dialog = false;
			},
			setRange() {
				if (!this.dates || this.dates.length === 0) {
					this.$emit("change", false);
					return;
				}
				const dates =
					typeof this.dates === "string"
						? [this.dates]
						: Array.from(this.dates).sort();
				if (dates.length === 1) dates.push(dates[0]);

				if (!this.startTime && !this.endTime) {
					dates[0] += " 00:00:00";
					dates[1] += " 23:59:59";
				} else if (!this.startTime) {
					this.startErr = true;
					this.errorMessage = "start time is not valid";
					this.$emit("change", false);
					return;
				} else if (!this.endTime) {
					this.endErr = true;
					this.errorMessage = "end time is not valid";
					this.$emit("change", false);
					return;
				} else if (
					dates[0] === dates[1] &&
					this.startTime >= this.endTime
				) {
					this.startErr = true;
					this.endErr = true;
					this.errorMessage = "start time is greater than end time";
					return;
				} else {
					dates[0] += ` ${this.startTime}`;
					dates[1] += ` ${this.endTime}`;
				}

				const res = dates.map(val => {
					const res = moment(val)
						.utc()
						.format();
					return res//.substring(0, res.length - 1);
				});

				this.$emit("change", res);
				this.menu = false;
			},
			setDate() {
				if (this.singleDate || this.singleDate.length != 0) {
					this.$emit("singleDateSelected", this.singleDate);
				}
				else {
					this.$emit("singleDateSelected", false);
				}
				this.menu = false;
			},
			quickTime(btn) {
				let res = "";
				switch (btn.toUpperCase()) {
					case "TODAY":
						res = [
							moment()
								.startOf("day")
								.utc()
								.format()
								.split("Z")
								.join(""),
							moment()
								.endOf("day")
								.utc()
								.format()
								.split("Z")
								.join("")
						];
						break;
					case "YESTERDAY":
						res = [
							moment()
								.subtract(1, "days")
								.startOf("day")
								.utc()
								.format()
								.split("Z")
								.join(""),
							moment()
								.subtract(1, "days")
								.endOf("day")
								.utc()
								.format()
								.split("Z")
								.join("")
						];
						break;
					case "LAST 7 DAYS":
						res = [
							moment()
								.subtract(7, "days")
								.startOf("day")
								.utc()
								.format()
								.split("Z")
								.join(""),
							moment()
								.endOf("day")
								.utc()
								.format()
								.split("Z")
								.join("")
						];
						break;

					case "LAST 30 DAYS":
						res = [
							moment()
								.subtract(30, "days")
								.startOf("day")
								.utc()
								.format()
								.split("Z")
								.join(""),
							moment()
								.endOf("day")
								.utc()
								.format()
								.split("Z")
								.join("")
						];
						break;

					case "THIS MONTH":
						res = [
							moment()
								.startOf("month")
								.utc()
								.format()
								.split("Z")
								.join(""),
							moment()
								.endOf("day")
								.utc()
								.format()
								.split("Z")
								.join("")
						];
						break;

					case "LAST MONTH":
						res = [
							moment()
								.subtract(1, "months")
								.startOf("month")
								.utc()
								.format()
								.split("Z")
								.join(""),
							moment()
								.subtract(1, "months")
								.endOf("month")
								.utc()
								.format()
								.split("Z")
								.join("")
						];
						break;

					default:
						break;
				}

				this.dates = res.map(date => date.split("T")[0]);
				this.startTime = null;
				this.endTime = null;
				this.$emit("change", res);
				this.menu = false;
			}
		}
	};
</script>

<style lang="scss">
	.date-time-range-picker {
		.v-picker__actions.v-card__actions {
			flex-wrap: wrap;
		}
		.time-range {
			max-width: unset;
		}
		.shortcuts {
			position: absolute;
			top: 120px;
			left: 20px;
			width: 130px;
			height: 280px;
			z-index: 2;
			display: inline-grid;
		}
		.shrinkHeight {
			height: 130px !important;
		}
	}
	.control-container {
		padding: 0 1em;
	}

	.white-theme .v-icon {
		color: white;
	}

	.white-theme input {
		color: white !important;
	}
	// .v-menu__content {
	// 	overflow-y: hidden !important;
  // 	overflow-x: hidden !important;
	// }
</style>